// Design tokens for the Dragonsframe auth experience, transcribed from the Claude Design
// handoff (# Dragonsframe Auth Design). Editorial / print aesthetic: ink + ember + amber,
// Bricolage display, Hanken body, Space Mono labels. Dark + light variants.

export const SPARK_PATH =
  'M12 0C13 7.5 16.5 10.5 24 12 16.5 13.5 13 16.5 12 24 11 16.5 7.5 13.5 0 12 7.5 10.5 11 7.5 12 0Z';

export const FONT_DISPLAY = "'Bricolage Grotesque', system-ui, sans-serif";
export const FONT_BODY = "'Hanken Grotesk', system-ui, sans-serif";
export const FONT_MONO = "'Space Mono', monospace";

// Fractal-noise grain used as a subtle overlay on the auth surfaces.
export const NOISE_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

export type AuthTokens = {
  bg: string;
  bg2: string;
  text: string;
  muted: string;
  line: string;
  field: string;
  ember: string;
  amber: string;
  emberSoft: string;
  onEmber: string;
  err: string;
  shadow: string;
  pink: string;
  glow1: string;
  glow2: string;
  panelBg: string;
  panelShadow: string;
  noiseBlend: 'overlay' | 'multiply';
  noiseOpacity: number;
};

const dark: AuthTokens = {
  bg: '#15110E', bg2: '#1E1813', text: '#F5ECE0', muted: '#A99B8B',
  line: 'rgba(245,236,224,0.16)', field: 'rgba(245,236,224,0.045)',
  ember: '#FF5B2E', amber: '#FFB23E', emberSoft: 'rgba(255,91,46,0.16)',
  onEmber: '#1A0E07', err: '#FF6B5A', shadow: 'rgba(255,91,46,0.5)', pink: '#FF2D6B',
  glow1: 'rgba(255,91,46,0.20)', glow2: 'rgba(255,178,62,0.12)',
  panelBg: 'rgba(255,255,255,0.025)', panelShadow: '0 30px 70px -40px rgba(0,0,0,0.7)',
  noiseBlend: 'overlay', noiseOpacity: 0.55,
};

const light: AuthTokens = {
  bg: '#FBF5EC', bg2: '#FFFFFF', text: '#1C140D', muted: '#7A6A59',
  line: 'rgba(28,20,13,0.16)', field: 'rgba(28,20,13,0.035)',
  ember: '#E0431A', amber: '#C76A06', emberSoft: 'rgba(224,67,26,0.10)',
  onEmber: '#FFF6EE', err: '#C2331B', shadow: 'rgba(224,67,26,0.3)', pink: '#D81E63',
  glow1: 'rgba(224,67,26,0.10)', glow2: 'rgba(217,129,11,0.09)',
  panelBg: '#FFFFFF', panelShadow: '0 40px 80px -44px rgba(28,20,13,0.3)',
  noiseBlend: 'multiply', noiseOpacity: 0.4,
};

export function authTokens(mode: 'light' | 'dark'): AuthTokens {
  return mode === 'dark' ? dark : light;
}

/** The primary CTA gradient (amber → ember → pink). */
export function emberGradient(t: AuthTokens): string {
  return `linear-gradient(115deg, ${t.amber} 0%, ${t.ember} 56%, ${t.pink} 125%)`;
}

/** Desktop background mesh (radial ember/amber glows over ink/paper). */
export function deskMesh(t: AuthTokens): string {
  return `radial-gradient(900px 640px at 8% -10%, ${t.glow1}, transparent 60%), radial-gradient(780px 580px at 110% 114%, ${t.glow2}, transparent 58%), ${t.bg}`;
}
